import * as Icons from '@ant-design/icons';
import ConfirmationModal from '../../../components/modal/ConfirmationModal';
import FilterForm from 'components/forms/FilterForm';
import React, { useCallback, useState } from 'react';
import {
  Button,
  DatePicker,
  Form,
  Input,
  Space,
  Table,
  Typography
} from 'antd';
import { ENROLLMENT_STATUS } from 'constants/admin/enrollments';
import {
  IClassroom,
  IClassroomEnrollment
} from '../../../interfaces/classrooms';
import { ImportAttendanceModal } from 'components/modal/attendance/ImportAttendanceModal';
import { Link } from 'react-router-dom';
import { TagField, TextField, useModal, useTable } from '@refinedev/antd';
import { UpdateAttendanceModal } from 'components/modal/attendance/UpdateAttendanceModal';
import { deleteClassroomEnrollments } from '../../../requests/classroom-enrollment';
import { formatDate, replaceTimeZone } from 'services/date';
import { getDisciplineStatusDisplay } from 'services/graduate-enrollment/discipline-status';
import { useCan, useInvalidate, useNotification } from '@refinedev/core';

interface IFilter {
  name: string;
  email: string;
  startsAfter: dayjs.Dayjs;
  endsBefore: dayjs.Dayjs;
}

export const ClassroomEnrollments = ({
  classroomId,
  classroom: _classroom
}: {
  classroomId: string;
  classroom: IClassroom;
}) => {
  const resource = `classrooms/${classroomId}/enrollments`;
  const [toBeUpdated, setToBeUpdated] = useState<IClassroomEnrollment>();
  const [toBeRemoved, setToBeRemoved] = useState<React.Key[]>([]);
  const [toBeRemovedSingle, setToBeRemovedSingle] = useState<React.Key | null>(
    null
  );
  const { show, modalProps, close } = useModal();
  const {
    show: showAttendanceModal,
    modalProps: attendanceModalProps,
    close: closeAttendanceModal
  } = useModal();
  const {
    show: showSingleDeleteModal,
    modalProps: singleDeleteModalProps,
    close: closeSingleDeleteModal
  } = useModal();
  const {
    show: showImportAttendanceModal,
    modalProps: importAttendanceModalProps,
    close: closeImportAttendanceModal
  } = useModal();
  const { open } = useNotification();
  const { data: canDeleteEnrollment } = useCan({
    resource: 'classroom_enrollments',
    action: 'delete'
  });
  const { data: canDeleteClassroom } = useCan({
    resource: 'classrooms',
    action: 'delete'
  });
  const canUnenroll = Boolean(
    canDeleteEnrollment?.can || canDeleteClassroom?.can
  );
  const { data: canBulkUpdateAttendance } = useCan({
    resource: 'classroom_enrollments',
    action: 'bulk_update_attendance'
  });
  const { data: canCreateClassroom } = useCan({
    resource: 'classrooms',
    action: 'create'
  });
  // PROD3-231 — espelha Admin::Classrooms::EnrollmentPolicy#bulk_update_attendance?.
  // Sem isso o botão aparecia para qualquer um que abrisse a turma, e o 403 do
  // backend voltava como "Não foi possível realizar o upload do arquivo".
  const canImportAttendance = Boolean(
    canBulkUpdateAttendance?.can || canCreateClassroom?.can
  );
  const {
    tableProps,
    tableQueryResult: { refetch },
    searchFormProps
  } = useTable({
    resource,
    onSearch: ({ name, email, startsAfter, endsBefore }: IFilter) => {
      return [
        {
          field: 'filter[search_user_name]',
          operator: 'eq',
          value: name
        },
        {
          field: 'filter[by_user_email]',
          operator: 'eq',
          value: email
        },
        {
          field: 'filter[course_enrollment_created_after]',
          operator: 'eq',
          value: startsAfter && replaceTimeZone(startsAfter)
        },
        {
          field: 'filter[course_enrollment_created_before]',
          operator: 'eq',
          value: endsBefore && replaceTimeZone(endsBefore)
        }
      ];
    },

    filters: {
      initial: [
        {
          field: 'filter[enrolled]',
          operator: 'eq',
          value: true
        }
      ],
      permanent: [
        {
          field: 'filter[by_enrollment_status]',
          operator: 'eq',
          value: ENROLLMENT_STATUS.ACTIVE
        }
      ]
    }
  });

  const onSelectChange = (ids: React.Key[]) => {
    setToBeRemoved(ids);
  };

  const invalidate = useInvalidate();

  const rowSelection = {
    selectedRowKeys: toBeRemoved,
    onChange: onSelectChange,
    getCheckboxProps: (record: IClassroomEnrollment) => ({
      disabled: !canUnenroll || !!record.course_enrollment?.completed_at
    })
  };

  const handleBatchDelete = async () => {
    await handleDelete();
    close();
  };

  const hasSelected = toBeRemoved.length > 0;

  const handleSingleDelete = async () => {
    if (!toBeRemovedSingle) {
      return;
    }

    await handleDelete([toBeRemovedSingle]);
    closeSingleDeleteModal();
    setToBeRemovedSingle(null);
  };

  const handleDelete = async (ids = toBeRemoved) => {
    try {
      await deleteClassroomEnrollments({
        classroomId,
        classroom_enrollment_ids: ids
      });
      invalidate({
        resource: `classrooms/${classroomId}/available_enrollments`,
        invalidates: ['list']
      });
      setToBeRemoved([]);
      refetch();
    } catch {
      open?.({
        type: 'error',
        description: 'Ops! Algo deu errado.',
        message:
          'Não foi possível desmatricular o aluno, por favor tente novamente.'
      });
    }
  };

  const handleUpdateAttendanceRate = useCallback(
    (classromEnrrolment: IClassroomEnrollment) => () => {
      setToBeUpdated(classromEnrrolment);
      showAttendanceModal();
    },
    [showAttendanceModal]
  );

  return (
    <>
      <UpdateAttendanceModal
        modalClose={closeAttendanceModal}
        modalProps={attendanceModalProps}
        refetch={refetch}
        classroomEnrollment={toBeUpdated}
        resourceType="ClassroomEnrollment"
      />
      <ImportAttendanceModal
        modalClose={closeImportAttendanceModal}
        modalProps={importAttendanceModalProps}
        refetch={refetch}
        classroomId={classroomId}
      />
      <ConfirmationModal
        closable={false}
        okText="Confirmar"
        onOk={handleBatchDelete}
        bodyTitle="Desmatricular"
        bodyText="Ao confirmar essa ação, ela não poderá ser desfeita. Deseja continuar?"
        width={500}
        {...modalProps}
      />

      <ConfirmationModal
        closable={false}
        okText="Confirmar"
        onOk={handleSingleDelete}
        bodyTitle="Desmatricular"
        bodyText="Ao confirmar essa ação, ela não poderá ser desfeita. Deseja continuar?"
        width={500}
        {...singleDeleteModalProps}
      />

      <FilterForm searchFormProps={searchFormProps}>
        <Form.Item name="name">
          <Input placeholder="Nome do aluno" />
        </Form.Item>

        <Form.Item name="email">
          <Input placeholder="E-mail do aluno" />
        </Form.Item>

        <Form.Item name="startsAfter">
          <DatePicker
            className="w-full"
            placeholder="Início em"
            format="DD/MM/YYYY"
          />
        </Form.Item>

        <Form.Item name="endsBefore">
          <DatePicker
            className="w-full"
            placeholder="Término em"
            format="DD/MM/YYYY"
          />
        </Form.Item>
      </FilterForm>

      <Space align="center" className="my-4 justify-between w-full">
        <Typography.Title level={5} style={{ margin: 0, color: 'GrayText' }}>
          Alunos matriculados
        </Typography.Title>
        <Space>
          <Button
            onClick={showImportAttendanceModal}
            icon={<Icons.DownloadOutlined />}
            disabled={!canImportAttendance}
          >
            Importar Frequência
          </Button>
          <Button
            onClick={() => show()}
            disabled={!hasSelected || !canUnenroll}
          >
            Desmatricular
          </Button>
        </Space>
      </Space>
      <Table
        rowSelection={rowSelection}
        {...tableProps}
        pagination={{
          ...tableProps.pagination,
          totalBoundaryShowSizeChanger: 9
        }}
        rowKey="id"
      >
        <Table.Column<IClassroomEnrollment>
          dataIndex={['course_enrollment', 'user', 'full_name_or_name']}
          key="name"
          title="Nome"
          render={(value, record) => (
            <Link
              to={`/students_teachers/users/show/${record.course_enrollment.user.id}`}
            >
              {value}
            </Link>
          )}
        />
        <Table.Column
          dataIndex={['course_enrollment', 'user', 'email']}
          key="email"
          title="E-mail"
          render={value => <TextField value={value} />}
        />
        <Table.Column
          dataIndex={['course_enrollment', 'registration']}
          key="registration"
          title="Matrícula"
          render={value => <TextField value={value} />}
        />
        <Table.Column
          dataIndex={['course_enrollment', 'course', 'name']}
          key="course"
          title="Curso"
          render={value => <TextField value={value} />}
        />
        <Table.Column
          className="whitespace-nowrap"
          dataIndex={['course_enrollment', 'created_at']}
          key="course"
          title="Data início"
          render={value => (
            <TextField value={formatDate(value, 'DD/MM/YYYY')} />
          )}
        />
        <Table.Column<IClassroomEnrollment>
          dataIndex="attendance_rate"
          key="attendance_rate"
          title="Frequência"
          render={value => (
            <TextField value={value != null ? `${value} %` : '-'} />
          )}
        />
        <Table.Column<IClassroomEnrollment>
          key="academic_status"
          title="Situação"
          render={(_, record) => {
            const display = getDisciplineStatusDisplay(record);
            return <TagField color={display.color} value={display.label} />;
          }}
        />
        <Table.Column<IClassroomEnrollment>
          dataIndex="actions"
          key="actions"
          title="Ações"
          render={(_, record) => {
            return (
              <Space direction="horizontal">
                <Button
                  size="small"
                  onClick={handleUpdateAttendanceRate(record)}
                >
                  Editar Frequência
                </Button>
                <Button
                  size="small"
                  disabled={
                    !canUnenroll || !!record.course_enrollment?.completed_at
                  }
                  onClick={() => {
                    setToBeRemovedSingle(record.id);
                    showSingleDeleteModal();
                  }}
                >
                  Desmatricular
                </Button>
              </Space>
            );
          }}
        />
      </Table>
    </>
  );
};
